import { Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { Router } from '@angular/router';
import { filter, firstValueFrom, merge, timer } from 'rxjs';
import * as jwt_decode from 'jwt-decode';
import * as CryptoJS from 'crypto-js';
import { environment } from '../../environments/environment';
import { B2cConfigService } from './b2c-config.service';

@Injectable({
  providedIn: 'root'
})
export class UserAuthService {
  env = environment;
  key = CryptoJS.enc.Utf8.parse(this.env.encryptionKey);
  iv = CryptoJS.enc.Utf8.parse('8080808080808080');
  private readonly oauthTransientKeys = ['nonce', 'PKCE_verifier'];
  private isLoggingOut = false;
  /** Set by AppComponent from oauthService.tryLoginImplicitFlow() so callers can await the same in-flight call instead of racing it. */
  oauthReady: Promise<boolean> = Promise.resolve(false);
  constructor(
    private readonly oauthService: OAuthService,
    private readonly router: Router,
    private readonly b2cConfig: B2cConfigService
  ) { }

  getAccessToken() {
    let validToken = this.oauthService.getAccessToken();
    if (!validToken) {
      const encryptedToken = localStorage.getItem('SamlToken');
      if (encryptedToken) {
      validToken = this.decryptTokenUsingAES(encryptedToken);
      }
    }
    return validToken;
  }

  private isApplePlatform(): boolean {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod|macintosh|mac os x/.test(userAgent);
  }

  private readOAuthTransientKeys(storage: Storage): { [key: string]: string } {
    const values: { [key: string]: string } = {};
    this.oauthTransientKeys.forEach((key) => {
      const value = storage.getItem(key);
      if (value) {
        values[key] = value;
      }
    });
    return values;
  }

  private restoreOAuthTransientKeys(storage: Storage, values: { [key: string]: string }): void {
    Object.keys(values).forEach((key) => {
      storage.setItem(key, values[key]);
    });
  }

  /**
   * True when the current session belongs to the Sanlam Online platform: flagged
   * extension_TargetPlatform = SOL, or a token from the federated policy (extension_sanlam_guid).
   * Reads the id token claims, so it must be called before storage is cleared.
   */
  isSolUser(): boolean {
    const claims: any = this.getIdentityClaims() || {};
    const target = String(claims.extension_TargetPlatform ?? '').toLowerCase();
    if (target === 'scs') {
      return false;
    }
    // Claims are gone if another tab already logged out and cleared the shared storage, so fall
    // back to the policy this tab signed in with.
    return target === 'sol' || !!claims.extension_sanlam_guid || this.b2cConfig.currentKind() === 'sol';
  }

  /**
   * True when the session came through the SOL B2C policy, whatever platform the user is flagged as.
   * A user flagged SCS who signed in from /scs-consent is not a SOL user, but still came from Sanlam Online.
   * Reads the id token claims, so it must be called before storage is cleared.
   */
  signedInThroughSolPolicy(): boolean {
    const claims: any = this.getIdentityClaims() || {};
    return !!claims.extension_sanlam_guid || this.b2cConfig.currentKind() === 'sol';
  }

  hasValidSession(): boolean {
    if (this.isSamlUser()) {
      return true;
    }
    return this.oauthService.hasValidAccessToken();
  }

  logOut(url?: string) {
    if (this.isLoggingOut) {
       return;
    }
    this.isLoggingOut = true;

    const returnToSol = this.isSolUser() || this.signedInThroughSolPolicy();

    const preserveForApple = this.isApplePlatform();
    const preservedLocalOAuth = preserveForApple ? this.readOAuthTransientKeys(localStorage) : {};
    const preservedSessionOAuth = preserveForApple ? this.readOAuthTransientKeys(sessionStorage) : {};

    try {
      localStorage.clear();
    } catch (e) {
    }
    try {
      sessionStorage.clear();
    } catch (e) {
    }

    const restoreAppleKeys = () => {
      if (!preserveForApple) {
        return;
      }
      try {
        this.restoreOAuthTransientKeys(localStorage, preservedLocalOAuth);
      } catch (e) {
      }
      try {
        this.restoreOAuthTransientKeys(sessionStorage, preservedSessionOAuth);
      } catch (e) {
      }
    };

    restoreAppleKeys();

    if (this.isSamlUser()) {
      localStorage.removeItem('SamlToken');
      globalThis.location.replace('login');
      return;
    }

    if (returnToSol) {
      if (this.b2cConfig.currentKind() !== 'sol') {
        const solLoginUrl = this.b2cConfig.configFor('sol').authConfig?.loginUrl;
        if (solLoginUrl) {
          this.oauthService.logoutUrl = solLoginUrl.replace(/authorize$/i, 'logout');
        }
      }
      if (this.env.solLogoutUrl) {
        this.oauthService.postLogoutRedirectUri = this.env.solIdpLogoutUrl
          ? `${this.env.solIdpLogoutUrl}?post_logout_redirect_uri=${encodeURIComponent(this.env.solLogoutUrl)}`
          : this.env.solLogoutUrl;
      }
    }

    this.oauthService.logOut();

    restoreAppleKeys();
  }

  async logOutWhenReady(timeoutMs = 3000): Promise<void> {
    const isSaml = this.isSamlUser();
    if (!isSaml && !this.oauthService.logoutUrl) {
      await firstValueFrom(merge(
        this.oauthService.events.pipe(filter(event => event.type === 'discovery_document_loaded')),
        timer(timeoutMs)
      ));
    }

    this.logOut();

    if (!isSaml && !this.oauthService.logoutUrl) {
      this.router.navigateByUrl('/login', { replaceUrl: true });
    }
  }

  isSamlUser() {
    const validToken = localStorage.getItem('SamlToken');
    if (validToken) {
      return true;
    }
    return false;
  }

  getIdentityClaims(){
    if (this.isSamlUser()) {
      const decoded = jwt_decode(this.getAccessToken());
      return {
        given_name: decoded.firstName,
        name: decoded.name,
        family_name: decoded.surname
      };
    }
    return this.oauthService.getIdentityClaims();
  }

  getCurrentNudgeUserKey(): string {
    const claims: any = this.getIdentityClaims() || {};
    const claimUserId = claims.sub || claims.oid || claims.nameid || claims.name;
    if (claimUserId) {
      return `user-${String(claimUserId)}`;
    }

    const gemini = Number(localStorage.getItem('GeminiNumber')) || 0;
    return gemini > 0 ? `gemini-${gemini}` : 'anonymous';
  }

  decryptTokenUsingAES(encryptedToken) {
    const decryptedToken =  CryptoJS.AES.decrypt(encryptedToken, this.key,
      {
          keySize: 128 / 8,
          iv: this.iv,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7
      });
    return decryptedToken.toString(CryptoJS.enc.Utf8);
  }
  
  logoutActiveUser(url: string) {
    if (this.getAccessToken()) {
      this.logOut(url);
    }
  }
}
